import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FormControl, makeStyles, TextField, Button, InputAdornment, CircularProgress } from "@material-ui/core"
import { useFormik } from "formik"
import * as yup from "yup"
import "./index.css"
import { getUploadFileSummaryClear, getUploadFileSummaryRequest, uploadFileRequest, uploadFileRequestClear } from "../../actions/bifrostActions"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		margin: theme.spacing(2),
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
		// borderTop: `1px solid ${theme.palette.grey[300]}`,
		// margin: "16px 0",
		// paddingTop: "16px",
	},
	formActionsBtn: {
		margin: "0.5rem",
		"&:disabled": {
			backgroundColor: "#e0e0e0 !important",
		},
		textTransform: "initial",
		fontSize: "14px",
		padding: "8px 12px",
		backgroundColor: theme.palette.primary.main,
	},
	fileUploadWrapper: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: "center",
		margin: theme.spacing(2),
	},
	fileDetailsWrapper: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: "center",
		margin: theme.spacing(2),
	},
	loadingSection:{
		width: "100%",
		textAlign: 'center'
	},
	inputField:{
		"& .MuiInputBase-input.Mui-disabled": {
			background: 'rgba(44, 53, 51, 0.04)',
			color: theme.palette.grey.D100
		}
	},
	showSummaryBtn:{
		textTransform: "initial",
		fontSize: "14px",
		height: "30px",
		color: theme.palette.primary.dark,
		border: `solid 1px ${theme.palette.primary.dark}`
	},
	dividerLine: {
		width: "100%",
		border: `1px solid ${theme.palette.grey[400]}`,
	},
	cancelBtn:{
		textTransform: "initial",
		fontSize: "16px",
		color: theme.palette.grey.D100,
		fontWeight: '400'
	}
}))

//form validation
const validationSchema = yup.object({
	case_id: yup.string(""),
	name: yup.string("Enter case name").required("Case name is required"),
	root_path: yup.string("Enter upload path").required("Upload path is required").matches(/^[A-Za-z-_/]+/gi,'URL path should not start with numbers'),
})

const UploadFiles = ({ data, callback, refreshCasePage }) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()

	const isLoading = useSelector((state) => state.bifrostCase.isUploading)
	const isSaved = useSelector((state) => state.bifrostCase.upload)
	const isUploadFileSummaryLoading =  useSelector((state) => state.bifrostCase.isUploadFileSummaryLoading)
	const uploadFileSummaryDetails =  useSelector((state) => state.bifrostCase.uploadFileSummaryDetails)
	const uploadFileSummaryDetailsError = useSelector((state) => state.bifrostCase.uploadFileSummaryDetailsError)

	const [showUploadDetails,setShowUploadDetails] = useState(false)
	const [uploadingFileCount,setUploadingFileCount] = useState(0)
	const [uploadingFileSize,setUploadingFileSize] = useState(0)

	useEffect(() => {
		if(uploadFileSummaryDetails && Object.keys(uploadFileSummaryDetails).length){
			setUploadingFileCount(uploadFileSummaryDetails?.before_upload_preview?.total_files_count)
			setUploadingFileSize(uploadFileSummaryDetails?.before_upload_preview?.overall_size)
		} else{
			setUploadingFileCount(0)
			setUploadingFileSize(0)
		}
	},[uploadFileSummaryDetails])

	const formik = useFormik({
		initialValues: {
			case_id: data.case_id,
			name: data.name,
			root_path: "",
		},
		validationSchema: validationSchema,
		onSubmit: (values) => {
			const payload = {
				name: values.name,
				case_id: values.case_id,
				upload_config: {
					source_location: {
						type: "Local",
						root_path: values.root_path,
						credentials: "string",
					},
				},
				refreshCasePage
			}
			dispatch(uploadFileRequest(payload))
		},
	})

	useEffect(() => {
		if (isSaved[data?.case_id]?.isUploaded) {
			formik.resetForm()
			callback(true)
			history.push(`/bifrost/case/${data.case_id}`)
			dispatch(uploadFileRequestClear(data.case_id))
		}
	}, [isSaved])

	const getUploadFileSummary = () => {
		dispatch(getUploadFileSummaryClear())
		setShowUploadDetails(true)
		const payload = {
			type : 'Local',
			root_path: formik.values.root_path,
		}
		dispatch(getUploadFileSummaryRequest(payload))
	}

	return (
		<form className={classes.root} noValidate autoComplete="off" id="upload-files" onSubmit={formik.handleSubmit}>
			<FormControl fullWidth className={classes.margin}>
				<TextField
					id="name"
					label="Case name*"
					variant="outlined"
					value={formik.values.name}
					onChange={formik.handleChange}
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name && formik.errors.name}
					className={classes.inputField}
					disabled
				/>
			</FormControl>

			<FormControl fullWidth className={classes.fileUploadWrapper}>
				<TextField
					id="root_path"
					label="Upload Path"
					variant="outlined"
					placeholder="Provide path to upload documents"
					value={formik.values.root_path}
					onChange={formik.handleChange}
					error={formik.touched.root_path && Boolean(formik.errors.root_path || isSaved[data?.case_id]?.error) || uploadFileSummaryDetailsError}
					helperText={(formik.touched.root_path && formik.errors.root_path) || isSaved[data?.case_id]?.error  || uploadFileSummaryDetailsError}
					autoFocus
					style={{width:"390px"}}
					className={classes.inputField}
				/>
				<Button
					variant="outlined"
					size="small"
					onClick={() => getUploadFileSummary()}
					className={classes.showSummaryBtn}
					disabled={!(formik.isValid && formik.dirty)}
				>
					Show Summary
				</Button>
			</FormControl>
			<FormControl fullWidth className={`${showUploadDetails ? classes.fileDetailsWrapper:''}`}>
				{ showUploadDetails && (
						isUploadFileSummaryLoading ? (
							<section className={classes.loadingSection}>
								<CircularProgress size={20} />
							</section>
						):(
							<>
								<TextField
									id="fileCount"
									label="Total file count"
									variant="outlined"
									value={uploadingFileCount}
									helperText={formik.touched.name && formik.errors.name}
									className={classes.inputField}
									disabled
								/>
								<TextField
									id="fileSize"
									label="Total file size"
									variant="outlined"
									value={uploadingFileSize}
									helperText={formik.touched.name && formik.errors.name}
									className={classes.inputField}
									disabled
								/>
							</>
						)
					)
				}
			</FormControl>
			<hr className={classes.dividerLine} />
			<FormControl className={classes.formActions}>
				<Button 
					variant="text" 
					size="small"
					onClick={() => callback(false)}
					className={classes.cancelBtn}
				>
				Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					classes={classes}
					className={classes.formActionsBtn}
					type="submit"
					disabled={!(formik.isValid && formik.dirty)}
				>
					{isLoading ? "Uploading..." : "Start uploading"}
				</Button>
			</FormControl>
		</form>
	)
}

export default UploadFiles
